import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from '@/components/Helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, ExternalLink, ShieldCheck, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

type ClaimStatus = {
  siteId: string;
  siteUrl: string;
  domainId: string | null;
  transferStatus: string;
  transferEmail: string | null;
  transferRequestedAt: string | null;
  transferClaimStatus: string;
  transferClaimedAt: string | null;
  onboardingStatus: string;
  canClaim: boolean;
  alreadyOwnedByCurrentUser: boolean;
  currentUserEmail: string;
};

const formatDateTime = (value: string | null) => {
  if (!value) return 'Not available';
  return new Date(value).toLocaleString();
};

export default function ClaimGuardTransfer() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const siteId = useMemo(() => (searchParams.get('site') || '').trim(), [searchParams]);

  const loadStatus = useCallback(async () => {
    if (!siteId) {
      setError('A transfer site reference is required.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('guard-transfer-claim-status', {
        body: { siteId },
      });
      if (fnError || data?.error) {
        throw new Error(fnError?.message || data?.error || 'Unable to load transfer.');
      }

      setStatus(data as ClaimStatus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load transfer.');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const handleClaim = async () => {
    if (!siteId) return;
    setClaiming(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('guard-transfer-claim', {
        body: { siteId },
      });
      if (fnError || data?.error) {
        throw new Error(fnError?.message || data?.error || 'Unable to claim site.');
      }

      toast.success('Transfer claimed. This site connection is now linked to your ShadowScan account.');
      await loadStatus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to claim site.');
    } finally {
      setClaiming(false);
    }
  };

  const dashboardTarget =
    status?.domainId ? `/portal/domains/${status.domainId}` : '/portal';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Claim Guard Transfer | ShadowScan</title>
        <meta
          name="description"
          content="Claim a site transfer from ShadowScan Guard into your ShadowScan portal account."
        />
      </Helmet>

      <Card className="w-full max-w-2xl cyber-card">
        <CardHeader>
          <CardTitle>Claim Guard Transfer</CardTitle>
          <CardDescription>
            Guard remains the active panel until the transfer is claimed in ShadowScan.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading transfer details...
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Transfer unavailable</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : status ? (
            <>
              <div className="space-y-2 rounded-lg border border-border/70 p-4">
                <p className="text-sm text-muted-foreground">Site</p>
                <p className="font-mono text-base text-foreground">{status.siteUrl}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={status.transferStatus === 'transferred' ? 'default' : 'secondary'}>
                    {status.transferStatus === 'transferred' ? 'Transferred' : 'Awaiting claim'}
                  </Badge>
                  <Badge variant="outline">{status.transferClaimStatus.replace(/_/g, ' ')}</Badge>
                </div>
              </div>

              <div className="grid gap-3 rounded-lg border border-border/70 p-4 text-sm text-muted-foreground md:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em]">Claim email</p>
                  <p className="mt-1 font-medium text-foreground">{status.transferEmail || 'Not recorded'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em]">Requested</p>
                  <p className="mt-1 font-medium text-foreground">{formatDateTime(status.transferRequestedAt)}</p>
                </div>
              </div>

              {status.transferStatus === 'transferred' || status.alreadyOwnedByCurrentUser ? (
                <Alert>
                  <ShieldCheck className="h-4 w-4" />
                  <AlertTitle>Transfer claimed</AlertTitle>
                  <AlertDescription>
                    This site connection is now linked to <strong>{status.currentUserEmail || user?.email}</strong>.
                    {status.domainId
                      ? ' Continue into the portal to manage the linked domain.'
                      : ' If the site does not appear in the standard domains dashboard yet, add or link the domain from the portal when you are ready.'}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <ExternalLink className="h-4 w-4" />
                  <AlertTitle>Before you claim</AlertTitle>
                  <AlertDescription>
                    Claiming this transfer moves the paired site connection out of Guard and into this ShadowScan account. Guard stops being the active panel after the claim completes.
                  </AlertDescription>
                </Alert>
              )}

              {!status.canClaim && status.transferStatus !== 'transferred' && !status.alreadyOwnedByCurrentUser && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Claim not ready</AlertTitle>
                  <AlertDescription>
                    Complete ShadowScan account onboarding first, then return here to claim this site transfer.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap justify-end gap-2">
                <Button asChild variant="outline">
                  <Link to="/portal">Back to portal</Link>
                </Button>
                {status.transferStatus === 'transferred' || status.alreadyOwnedByCurrentUser ? (
                  <Button asChild>
                    <Link to={dashboardTarget}>Continue</Link>
                  </Button>
                ) : (
                  <Button onClick={handleClaim} disabled={claiming || !status.canClaim}>
                    {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                    Claim site
                  </Button>
                )}
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
